import api from './api';

export interface TestEventInfo {
  deviceId: string;
  supportedEventTypes: string[];
  virtualDevices: {
    online: string;
    offline: string;
  };
}

export interface VirtualSnapshotTopic {
  integrationId: number;
  appName: string;
  teamId: string;
  topic: string;
}

export interface VirtualSnapshotDevice {
  deviceId: string;
  payload: Record<string, unknown>;
  topics: VirtualSnapshotTopic[];
}

export interface VirtualSnapshotsResult {
  success: boolean;
  devices: VirtualSnapshotDevice[];
}

export interface TestEventWebhookTarget {
  id: string;
  name: string;
  url: string;
}

export interface TestEventMqttTarget {
  id: number;
  appName: string;
  teamId: string;
  topic: string;
}

export interface TestEventResult {
  success: boolean;
  eventId: string;
  deviceId: string;
  eventType: string;
  isTest: boolean;
  // Which channels this event actually fanned out over. Bridge-synthetic
  // events (command.queued, command.failed.bridge) are MQTT-only, so the UI
  // should hide the webhook tab when 'webhook' isn't present.
  channels: string[];
  payloads: {
    // null when this event type doesn't fan out to webhooks.
    webhook: Record<string, unknown> | null;
    mqtt: Record<string, unknown>;
  };
  targets: {
    webhooks: TestEventWebhookTarget[];
    mqtt: TestEventMqttTarget[];
  };
}

// companyId is sent on every call: the API ignores it for company admins
// (they're pinned to their own company) and requires it for super admins,
// whose company picker supplies it.
export async function getTestEventInfo(companyId: string): Promise<TestEventInfo> {
  const response = await api.get<TestEventInfo>('/v1/test-events', { params: { companyId } });
  return response.data;
}

export async function fireTestEvent(eventType: string, companyId: string): Promise<TestEventResult> {
  const response = await api.post<TestEventResult>('/v1/test-events', { eventType, companyId });
  return response.data;
}

export interface TestStateResult {
  success: boolean;
  deviceId: string;
  payload: Record<string, unknown>;
  targets: { mqtt: TestEventMqttTarget[] };
  restEndpoint: string;
}

export async function fireTestStateUpdate(companyId: string): Promise<TestStateResult> {
  const response = await api.post<TestStateResult>('/v1/test-events/state', { companyId });
  return response.data;
}

export async function publishVirtualSnapshots(companyId: string): Promise<VirtualSnapshotsResult> {
  const response = await api.post<VirtualSnapshotsResult>('/v1/test-events/virtual-devices/publish', { companyId });
  return response.data;
}

// v2 REST reads for the Testing tab. All of these work for the fixed
// test/virtual device IDs too — the API serves those without a DB row.

export async function getV2DeviceResource(deviceId: string): Promise<Record<string, unknown>> {
  const response = await api.get<Record<string, unknown>>(`/v2/devices/${deviceId}`);
  return response.data;
}

export async function getV2Devices(limit = 10): Promise<Record<string, unknown>> {
  const response = await api.get<Record<string, unknown>>('/v2/devices', { params: { limit } });
  return response.data;
}

export async function getV2DeviceCommands(deviceId: string): Promise<Record<string, unknown>> {
  const response = await api.get<Record<string, unknown>>(`/v2/devices/${deviceId}/commands`);
  return response.data;
}
